import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import {
    login,
    logout,
    clearError,
    refreshToken,
    setupMFA,
    sendMFA,
    verifyMFA,
    getUserRBAC,
} from '../store/authSlice';
import { logout as logoutApi } from '../services/auth';
import type { LoginCredentials, MFAVerificationData } from '../types/auth.types';
import type { MfaSetupRequest } from '../services/mfa';
import { fetchDealerConfig } from '@features/dealers/store/dealerConfigSlice';

export const useAuth = () => {
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const auth = useAppSelector((state) => state.auth);

    // FIRE-AND-FORGET, deliberately. handleRefreshRAC() above THROWS on failure and
    // that is correct — RBAC gates the whole app. Dealer config gates one UI default
    // and must never be able to fail a login, so this is dispatched without await
    // and without a .catch() rethrow. `fetchDealerConfig` is a createAsyncThunk, so a
    // rejection resolves to a rejected ACTION (status -> 'error'), never an unhandled
    // promise rejection. `resolvePrepopulateMileage` then returns false, which is the
    // default-OFF rule (plan D6). See plan D9.
    const prefetchHomeDealerConfig = (dealerId: string | null) => {
        // RECORDING the home dealer id is NOT this helper's job — the authSlice
        // reducer for this thunk already did it (plan D8/G24), which is the only
        // wiring that survives a page reload. This helper only WARMS the cache, so
        // it does nothing at all when the claim is null.
        if (dealerId) {
            void dispatch(fetchDealerConfig(dealerId));
        }
    };

    const handleLogin = async (credentials: LoginCredentials) => {
        const result = await dispatch(login(credentials));

        if (login.fulfilled.match(result)) {
            await handleRefreshRAC();
            prefetchHomeDealerConfig(result.payload.dealer_id);
            return result.payload;
        }
        throw new Error(result.payload as string);
    };

    // The dealer config cache is NOT cleared here. `dealerConfigSlice` reacts to the
    // `logout` action itself, so the reset also covers the global 401 "Invalid Token"
    // path in the Axios interceptor, which calls `authUtils.handleLogout()` and never
    // reaches this hook. Dispatching clearDealerConfig() here as well would only make
    // this hook look like the mechanism when it is not. Plan D5.
    const handleLogout = () => {
        dispatch(logout());
        queryClient.clear();
    };

    const handleLogoutAndRedirect = async () => {
        try {
            await Promise.race([
                logoutApi(),
                new Promise<void>((resolve) => setTimeout(resolve, 1000)),
            ]);
        } catch (err) {
            console.warn('Server logout failed; local state cleared anyway', err);
        }

        // See handleLogout: the dealer config reset rides on the `logout` action.
        dispatch(logout());
        queryClient.clear();
        navigate('/login', { replace: true });
    };

    const handleClearError = () => {
        dispatch(clearError());
    };

    const handleRefreshToken = async () => {
        const result = await dispatch(refreshToken());
        if (refreshToken.fulfilled.match(result)) {
            await handleRefreshRAC();
            // The call site below is currently unreachable from application code
            // (G24): AuthProvider's startup refresh and useTokenRefresh's scheduled
            // refresh both dispatch `refreshToken` directly, bypassing this hook.
            // Wired for symmetry only — it must not be the mechanism anything
            // depends on. See plan D8/D9.
            prefetchHomeDealerConfig(result.payload.dealer_id);
            return result.payload;
        }
        throw new Error(result.payload as string);
    };

    const handleSetupMFA = async (mfaSetup: MfaSetupRequest) => {
        const result = await dispatch(setupMFA(mfaSetup));
        if (setupMFA.fulfilled.match(result)) {
            return result.payload;
        }
        throw new Error(result.payload as string);
    };

    const handleSendMFA = async () => {
        const result = await dispatch(sendMFA());
        if (sendMFA.fulfilled.match(result)) {
            return result.payload;
        }
        throw new Error(result.payload as string);
    };

    const handleVerifyMFA = async (data: MFAVerificationData) => {
        const result = await dispatch(verifyMFA(data));
        if (verifyMFA.fulfilled.match(result)) {
            // Best-effort RBAC hydrate so the upcoming ProtectedRoutes gate
            // resolves quickly. Never let an RBAC failure discard a
            // successfully verified MFA payload: first-time enrolment depends
            // on this resolution to navigate to /mfa/recovery-codes, and
            // AuthRoutes redirects authenticated users away from /mfa/*, so
            // a thrown error would lose the one-time codes. On RBAC failure
            // the slice's getUserRBAC.rejected reducer sets rbacStatus='error'
            // and ProtectedRoutes surfaces the retry CTA on the next
            // protected-route render.
            try {
                await handleRefreshRAC();
            } catch (err) {
                console.warn(
                    'RBAC hydration after MFA verify failed; MFA payload preserved, retry CTA will surface on next protected route render',
                    err,
                );
            }
            return result.payload;
        }
        throw new Error(result.payload as string);
    };

    const handleRefreshRAC = async () => {
        const result = await dispatch(getUserRBAC());
        if (getUserRBAC.fulfilled.match(result)) {
            return result.payload;
        }
        throw new Error(result.payload as string);
    };

    return {
        // State
        user: auth.user,
        isAuthenticated: auth.isAuthenticated,
        isInternal: auth.isInternal,
        isLoading: auth.isLoading,
        error: auth.error,
        mfaStatus: auth.mfa?.status || null,
        mfaMethod: auth.mfa?.method || null,
        mfaTotpQrCode: auth.mfa?.totpQrCode,
        mfaTotpSecret: auth.mfa?.totpSecret,

        // Actions
        login: handleLogin,
        logout: handleLogout,
        logoutAndRedirect: handleLogoutAndRedirect,
        clearError: handleClearError,
        refreshToken: handleRefreshToken,
        setupMFA: handleSetupMFA,
        sendMFA: handleSendMFA,
        verifyMFA: handleVerifyMFA,
    };
};
